import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';

const PLATFORMS = [
  'Zoom',
  'Google Meet',
  'Microsoft Teams',
  'Webex',
  'Discord',
  'Skype',
  'Other'
];

const WORKSHOP_CATEGORIES = [
  'Academic Writing',
  'College Applications',
  'Essay Writing',
  'Interview Prep',
  'Leadership',
  'STEM',
  'Business & Entrepreneurship',
  'Languages',
  'Test Preparation',
  'Portfolio Building',
  'Career Development',
  'Public Speaking',
  'Research & Innovation',
  'Time Management',
  'Other'
];

const WorkshopForm = ({ workshop, onClose }) => {
  const { user } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start_date: '',
    end_date: '',
    format: 'online',
    platform: 'Zoom',
    images: [],
    // Optional fields
    instructor: '',
    instructor_title: '',
    duration: '',
    difficulty: '',
    category: '',
    spots_left: 20,
    instructor_image: '',
    instructor_image_alt: '',
    address: '',
    skills: [],
    prerequisites: [],
    learning_outcomes: []
  });
  const [imageFiles, setImageFiles] = useState([]);
  const [organizerImageUploading, setOrganizerImageUploading] = useState(false);
  const [showImageCropper, setShowImageCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [cropperZoom, setCropperZoom] = useState(1);
  const [cropperPanX, setCropperPanX] = useState(0);
  const [cropperPanY, setCropperPanY] = useState(0);
  const [cropperRotation, setCropperRotation] = useState(0);
  const [cropShape, setCropShape] = useState('circle');
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [croppedResult, setCroppedResult] = useState(null);
  const [skillInput, setSkillInput] = useState('');
  const [prerequisiteInput, setPrerequisiteInput] = useState('');
  const [outcomeInput, setOutcomeInput] = useState('');

  useEffect(() => {
    if (workshop) {
      setFormData({
        title: workshop.title || '',
        description: workshop.description || '',
        start_date: workshop.start_date ? workshop.start_date.split('T')[0] + 'T' + workshop.start_date.split('T')[1].substring(0, 5) : '',
        end_date: workshop.end_date ? workshop.end_date.split('T')[0] + 'T' + workshop.end_date.split('T')[1].substring(0, 5) : '',
        format: workshop.format || 'online',
        platform: workshop.platform || 'Zoom',
        images: workshop.images || [],
        instructor: workshop.instructor || '',
        instructor_title: workshop.instructor_title || '',
        duration: workshop.duration || '',
        difficulty: workshop.difficulty || '',
        category: workshop.category || '',
        spots_left: workshop.spots_left ?? 20,
        instructor_image: workshop.instructor_image || '',
        instructor_image_alt: workshop.instructor_image_alt || '',
        address: workshop.address || '',
        skills: workshop.skills || [],
        prerequisites: workshop.prerequisites || [],
        learning_outcomes: workshop.learning_outcomes || []
      });
    }
  }, [workshop]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'spots_left' ? Number(value) : value
    }));
  };

  const calculateDuration = () => {
    if (!formData.start_date || !formData.end_date) return '';
    
    const start = new Date(formData.start_date);
    const end = new Date(formData.end_date);
    const diffMs = end - start;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (diffDays > 0) {
      return diffDays === 1 ? `1 day` : `${diffDays} days`;
    } else if (diffHours > 0) {
      return diffHours === 1 ? `1 hour` : `${diffHours} hours`;
    } else {
      return 'Same day';
    }
  };

  const handleOrganizerImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Show image cropper
    const reader = new FileReader();
    reader.onload = (event) => {
      setImageToCrop(event.target?.result);
      setShowImageCropper(true);
      setCropperZoom(1);
      setCropperPanX(0);
      setCropperPanY(0);
      setCropperRotation(0);
      setCropShape('circle');
      setIsDragging(false);
    };
    reader.readAsDataURL(file);
  };

  const adjustZoom = (nextZoom) => {
    setCropperZoom((prevZoom) => {
      const clamped = Math.min(3, Math.max(0.5, nextZoom));
      const factor = clamped / prevZoom;
      setCropperPanX((prevX) => prevX * factor);
      setCropperPanY((prevY) => prevY * factor);
      return clamped;
    });
  };

  const applyCrop = () => {
    if (!imageToCrop) return;

    // Create canvas and crop the image with rotation + mask
    const img = new Image();
    img.onload = () => {
      // Match canvas size to visible crop diameter for accurate export
      const CROP_SIZE = 280;
      const canvas = document.createElement('canvas');
      canvas.width = CROP_SIZE;
      canvas.height = CROP_SIZE;
      const ctx = canvas.getContext('2d');

      // Set white background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, CROP_SIZE, CROP_SIZE);

      // Translate to center, rotate, then draw scaled/panned image
      ctx.save();
      ctx.translate(CROP_SIZE / 2, CROP_SIZE / 2);
      ctx.rotate((cropperRotation * Math.PI) / 180);

      const scaledWidth = img.width * cropperZoom;
      const scaledHeight = img.height * cropperZoom;
      const drawX = -scaledWidth / 2 + cropperPanX;
      const drawY = -scaledHeight / 2 + cropperPanY;

      ctx.drawImage(img, drawX, drawY, scaledWidth, scaledHeight);
      ctx.restore();

      // Apply circular mask (avatar output)
      ctx.globalCompositeOperation = 'destination-in';
      ctx.beginPath();
      ctx.arc(CROP_SIZE / 2, CROP_SIZE / 2, CROP_SIZE / 2, 0, Math.PI * 2);
      ctx.fill();

      const croppedUrl = canvas.toDataURL('image/png');
      setCroppedResult(croppedUrl);
      uploadCroppedImage(croppedUrl);
    };
    img.src = imageToCrop;
  };

  const uploadCroppedImage = async (croppedUrl) => {
    setOrganizerImageUploading(true);
    try {
      const response = await fetch(croppedUrl);
      const blob = await response.blob();
      const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.jpg`;

      const { error: uploadError } = await supabase.storage
        .from('workshop-images')
        .upload(fileName, blob);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('workshop-images')
        .getPublicUrl(fileName);

      setFormData(prev => ({
        ...prev,
        instructor_image: publicUrl,
        instructor_image_alt: prev.instructor_image_alt || `${prev.instructor || 'Instructor'} photo`
      }));

      setShowImageCropper(false);
      setImageToCrop(null);
      setCroppedResult(null);
    } catch (error) {
      console.error('Error uploading instructor image:', error);
      alert('Failed to upload instructor image: ' + error.message);
    } finally {
      setOrganizerImageUploading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    const uploadedUrls = [];

    try {
      for (const file of files) {
        const fileExt = file.name.split('.').pop();
        const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;
        const filePath = `${fileName}`;

        const { error: uploadError, data } = await supabase.storage
          .from('workshop-images')
          .upload(filePath, file);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('workshop-images')
          .getPublicUrl(filePath);

        uploadedUrls.push(publicUrl);
      }

      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...uploadedUrls]
      }));
    } catch (error) {
      console.error('Error uploading images:', error);
      alert('Failed to upload images: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const addSkill = () => {
    if (skillInput.trim()) {
      setFormData(prev => ({
        ...prev,
        skills: [...prev.skills, skillInput.trim()]
      }));
      setSkillInput('');
    }
  };

  const removeSkill = (index) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.filter((_, i) => i !== index)
    }));
  };

  const addPrerequisite = () => {
    if (prerequisiteInput.trim()) {
      setFormData(prev => ({
        ...prev,
        prerequisites: [...prev.prerequisites, prerequisiteInput.trim()]
      }));
      setPrerequisiteInput('');
    }
  };

  const removePrerequisite = (index) => {
    setFormData(prev => ({
      ...prev,
      prerequisites: prev.prerequisites.filter((_, i) => i !== index)
    }));
  };

  const addOutcome = () => {
    if (outcomeInput.trim()) {
      setFormData(prev => ({
        ...prev,
        learning_outcomes: [...prev.learning_outcomes, outcomeInput.trim()]
      }));
      setOutcomeInput('');
    }
  };

  const removeOutcome = (index) => {
    setFormData(prev => ({
      ...prev,
      learning_outcomes: prev.learning_outcomes.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.description || !formData.start_date || !formData.end_date) {
      alert('Please fill in all required fields');
      return;
    }

    setSubmitting(true);

    try {
      const workshopData = {
        ...formData,
        duration: calculateDuration(),
        created_by: user.id,
        updated_at: new Date().toISOString()
      };

      if (workshop) {
        // Update existing workshop
        const { error } = await supabase
          .from('workshops')
          .update(workshopData)
          .eq('id', workshop.id);

        if (error) throw error;
        setSuccessMessage('Workshop updated successfully!');
        setShowSuccessDialog(true);
      } else {
        // Create new workshop
        const { error } = await supabase
          .from('workshops')
          .insert([workshopData]);

        if (error) throw error;
        setSuccessMessage('Workshop created successfully!');
        setShowSuccessDialog(true);
      }

      onClose();
    } catch (error) {
      console.error('Error saving workshop:', error);
      alert('Failed to save workshop: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">
          {workshop ? 'Edit Workshop' : 'Create New Workshop'}
        </h2>
        <button
          onClick={onClose}
          className="p-2 hover:bg-muted rounded-lg transition-colors"
        >
          <Icon name="X" className="w-5 h-5" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Image Upload */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Workshop Images
          </label>
          <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageUpload}
              className="hidden"
              id="image-upload"
              disabled={uploading}
            />
            <label
              htmlFor="image-upload"
              className="cursor-pointer inline-flex flex-col items-center"
            >
              <Icon name="Upload" className="w-8 h-8 text-muted-foreground mb-2" />
              <span className="text-sm text-muted-foreground">
                {uploading ? 'Uploading...' : 'Click to upload images'}
              </span>
            </label>
          </div>

          {/* Image Preview */}
          {formData.images.length > 0 && (
            <div className="grid grid-cols-3 gap-4 mt-4">
              {formData.images.map((url, index) => (
                <div key={index} className="relative group">
                  <img
                    src={url}
                    alt={`Workshop ${index + 1}`}
                    className="w-full h-32 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    className="absolute top-2 right-2 bg-destructive text-destructive-foreground p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Icon name="X" className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Title */}
        <div>
          <label htmlFor="title" className="block text-sm font-medium mb-2">
            Title <span className="text-destructive">*</span>
          </label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            required
            className="w-full px-4 py-2 bg-background text-foreground border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary placeholder:text-muted-foreground"
            placeholder="e.g., Web Development Bootcamp"
          />
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium mb-2">
            About / Description <span className="text-destructive">*</span>
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            required
            rows={9}
            className="w-full px-4 py-2 bg-background text-foreground border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary placeholder:text-muted-foreground"
            placeholder="Describe the workshop in detail..."
          />
        </div>

        {/* Dates */}
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="start_date" className="block text-sm font-medium mb-2">
              Start Date & Time <span className="text-destructive">*</span>
            </label>
            <input
              type="datetime-local"
              id="start_date"
              name="start_date"
              value={formData.start_date}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-2 bg-background text-foreground border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label htmlFor="end_date" className="block text-sm font-medium mb-2">
              End Date & Time <span className="text-destructive">*</span>
            </label>
            <input
              type="datetime-local"
              id="end_date"
              name="end_date"
              value={formData.end_date}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-2 bg-background text-foreground border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>

        {/* Format */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Format <span className="text-destructive">*</span>
          </label>
          <div className="flex gap-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="format"
                value="online"
                checked={formData.format === 'online'}
                onChange={handleInputChange}
                className="mr-2"
              />
              Online
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="format"
                value="physical"
                checked={formData.format === 'physical'}
                onChange={handleInputChange}
                className="mr-2"
              />
              Physical
            </label>
          </div>
        </div>

        {/* Platform (if online) */}
        {formData.format === 'online' && (
          <div>
            <label htmlFor="platform" className="block text-sm font-medium mb-2">
              Platform
            </label>
            <select
              id="platform"
              name="platform"
              value={formData.platform}
              onChange={handleInputChange}
              className="w-full px-4 py-2 bg-background text-foreground border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {PLATFORMS.map(platform => (
                <option key={platform} value={platform}>
                  {platform}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Address (if physical) */}
        {formData.format === 'physical' && (
          <div>
            <label htmlFor="address" className="block text-sm font-medium mb-2">
              Address <span className="text-destructive">*</span>
            </label>
            <textarea
              id="address"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              required={formData.format === 'physical'}
              rows={1}
              className="w-full px-4 py-2 bg-background text-foreground border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary placeholder:text-muted-foreground"
              placeholder="Enter the full address where the workshop will be held..."
            />
          </div>
        )}

        {/* Optional Fields */}
        <div className="border-t border-border pt-6">
          <h3 className="text-lg font-semibold mb-4">Optional Information</h3>
          
          <div className="space-y-4">
            {/* Instructor */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="instructor" className="block text-sm font-medium mb-2">
                  Instructor Name
                </label>
                <input
                  type="text"
                  id="instructor"
                  name="instructor"
                  value={formData.instructor}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-background text-foreground border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary placeholder:text-muted-foreground"
                  placeholder="e.g., Dr. Sarah Mitchell"
                />
              </div>
              <div>
                <label htmlFor="instructor_title" className="block text-sm font-medium mb-2">
                  Instructor Title
                </label>
                <input
                  type="text"
                  id="instructor_title"
                  name="instructor_title"
                  value={formData.instructor_title}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-background text-foreground border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary placeholder:text-muted-foreground"
                  placeholder="e.g., Senior Developer"
                />
              </div>
            </div>

            {/* Instructor photo */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Instructor Photo (optional)
              </label>
              <div className="flex items-center gap-3">
                <div className="w-16 h-16 rounded-full overflow-hidden border border-border bg-muted flex items-center justify-center">
                  {formData.instructor_image ? (
                    <img src={formData.instructor_image} alt={formData.instructor_image_alt || 'Instructor photo'} className="w-full h-full object-cover" />
                  ) : (
                    <Icon name="User" className="w-6 h-6 text-muted-foreground" />
                  )}
                </div>
                <div className="flex-1">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleOrganizerImageUpload}
                    disabled={organizerImageUploading}
                    className="block w-full text-sm text-muted-foreground"
                  />
                  <p className="text-xs text-muted-foreground mt-1">PNG/JPG, up to a few MB.</p>
                </div>
              </div>
            </div>

            {/* Duration, Difficulty, Category */}
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Duration
                </label>
                <div className="w-full px-4 py-2 border border-border rounded-lg bg-muted/50 flex items-center">
                  <span className="text-foreground">
                    {calculateDuration() || 'Select dates to calculate'}
                  </span>
                </div>
              </div>
              <div>
                <label htmlFor="difficulty" className="block text-sm font-medium mb-2">
                  Difficulty
                </label>
                <select
                  id="difficulty"
                  name="difficulty"
                  value={formData.difficulty}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-background text-foreground border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Not specified</option>
                  <option value="Beginner">Beginner</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Advanced">Advanced</option>
                </select>
              </div>
              <div>
                <label htmlFor="category" className="block text-sm font-medium mb-2">
                  Category
                </label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-background text-foreground border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Select a category</option>
                  {WORKSHOP_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="spots_left" className="block text-sm font-medium mb-2">
                  Spots Left
                </label>
                <input
                  type="number"
                  id="spots_left"
                  name="spots_left"
                  min="0"
                  value={formData.spots_left}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-background text-foreground border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary placeholder:text-muted-foreground"
                  placeholder="e.g., 20"
                />
              </div>
            </div>

            {/* Skills */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Skills Taught
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={skillInput}
                  onChange={(e) => setSkillInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                  className="flex-1 px-4 py-2 bg-background text-foreground border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary placeholder:text-muted-foreground"
                  placeholder="Add a skill..."
                />
                <Button type="button" onClick={addSkill} className="hover:bg-primary/90">
                  Add
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.skills.map((skill, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium"
                  >
                    {skill}
                    <button
                      type="button"
                      onClick={() => removeSkill(index)}
                      className="hover:text-destructive transition-colors"
                    >
                      <Icon name="X" className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Prerequisites */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Prerequisites
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={prerequisiteInput}
                  onChange={(e) => setPrerequisiteInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addPrerequisite())}
                  className="flex-1 px-4 py-2 bg-background text-foreground border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary placeholder:text-muted-foreground"
                  placeholder="Add a prerequisite..."
                />
                <Button type="button" onClick={addPrerequisite} className="hover:bg-primary/90">
                  Add
                </Button>
              </div>
              <div className="space-y-1">
                {formData.prerequisites.map((prereq, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <span className="text-sm flex-1">{prereq}</span>
                    <button
                      type="button"
                      onClick={() => removePrerequisite(index)}
                      className="hover:text-destructive transition-colors"
                    >
                      <Icon name="X" className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Learning Outcomes */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Learning Outcomes
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={outcomeInput}
                  onChange={(e) => setOutcomeInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addOutcome())}
                  className="flex-1 px-4 py-2 bg-background text-foreground border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary placeholder:text-muted-foreground"
                  placeholder="Add a learning outcome..."
                />
                <Button type="button" onClick={addOutcome} className="hover:bg-primary/90">
                  Add
                </Button>
              </div>
              <div className="space-y-1">
                {formData.learning_outcomes.map((outcome, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <span className="text-sm flex-1">{outcome}</span>
                    <button
                      type="button"
                      onClick={() => removeOutcome(index)}
                      className="hover:text-destructive transition-colors"
                    >
                      <Icon name="X" className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex gap-4 pt-6 border-t border-border">
          <Button
            type="submit"
            disabled={submitting || uploading}
            className="flex-1"
          >
            {submitting ? 'Saving...' : workshop ? 'Update Workshop' : 'Create Workshop'}
          </Button>
          <Button
            type="button"
            onClick={onClose}
            variant="outline"
            disabled={submitting || uploading}
          >
            Cancel
          </Button>
        </div>
      </form>

      {/* Image Cropper Modal */}
      {showImageCropper && imageToCrop && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-5xl w-full p-6 relative">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Image Crop & Rotate</h3>
              <button
                onClick={() => {
                  setShowImageCropper(false);
                  setImageToCrop(null);
                  setCroppedResult(null);
                  setCropperZoom(1);
                  setCropperPanX(0);
                  setCropperPanY(0);
                  setCropperRotation(0);
                  setCropShape('circle');
                }}
                className="p-2 rounded-md hover:bg-muted"
              >
                <Icon name="X" className="w-5 h-5" />
              </button>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              {/* Preview area */}
              <div className="flex justify-center">
                <div className="relative w-[360px] h-[360px] bg-muted/70 rounded-xl border border-border overflow-hidden">
                  {/* subtle grid background */}
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      backgroundImage:
                        'linear-gradient(to right, rgba(0,0,0,0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(0,0,0,0.05) 1px, transparent 1px)',
                      backgroundSize: '20px 20px'
                    }}
                  />

                  <div
                    className="absolute inset-4"
                    style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
                    onMouseDown={(e) => {
                      setIsDragging(true);
                      setDragStart({ x: e.clientX - cropperPanX, y: e.clientY - cropperPanY });
                    }}
                    onMouseMove={(e) => {
                      if (!isDragging) return;
                      const moveX = e.clientX - dragStart.x;
                      const moveY = e.clientY - dragStart.y;
                      setCropperPanX(moveX);
                      setCropperPanY(moveY);
                    }}
                    onMouseUp={() => setIsDragging(false)}
                    onMouseLeave={() => setIsDragging(false)}
                  >
                    <div className="relative w-full h-full">
                      <img
                        src={imageToCrop}
                        alt="Cropping preview"
                        draggable="false"
                        className="absolute top-1/2 left-1/2 max-w-none max-h-none object-contain select-none pointer-events-none"
                        style={{
                          transform: `translate(-50%, -50%) scale(${cropperZoom}) translate(${cropperPanX / cropperZoom}px, ${cropperPanY / cropperZoom}px) rotate(${cropperRotation}deg)`,
                          transformOrigin: 'center'
                        }}
                      />

                      {/* mask & handles */}
                      <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                        <div
                          className={cropShape === 'circle' ? 'relative w-[280px] h-[280px] rounded-full' : 'relative w-[280px] h-[280px] rounded-xl'}
                          style={{
                            boxShadow: '0 0 0 9999px rgba(0,0,0,0.45)',
                            border: '2px solid rgba(59,130,246,0.85)'
                          }}
                        >
                          {['tl','tr','bl','br'].map((pos) => {
                            const base = 'absolute w-3 h-3 bg-primary rounded-sm shadow-sm';
                            const map = {
                              tl: `${base} -top-2 -left-2`,
                              tr: `${base} -top-2 -right-2`,
                              bl: `${base} -bottom-2 -left-2`,
                              br: `${base} -bottom-2 -right-2`
                            };
                            return <span key={pos} className={map[pos]} />;
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Controls */}
              <div className="flex flex-col gap-4">
                {/* Zoom */}
                <div>
                  <label className="block text-sm font-medium mb-2">Zoom: {cropperZoom.toFixed(1)}x</label>
                  <div className="flex gap-2 items-center mb-2">
                    <Button
                      type="button"
                      onClick={() => adjustZoom(cropperZoom - 0.2)}
                      disabled={organizerImageUploading}
                      variant="outline"
                      className="px-3 py-1 text-sm"
                    >
                      <Icon name="Minus" className="w-4 h-4" />
                    </Button>
                    <input
                      type="range"
                      min="0.5"
                      max="3"
                      step="0.1"
                      value={cropperZoom}
                      onChange={(e) => adjustZoom(parseFloat(e.target.value))}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      onClick={() => adjustZoom(cropperZoom + 0.2)}
                      disabled={organizerImageUploading}
                      variant="outline"
                      className="px-3 py-1 text-sm"
                    >
                      <Icon name="Plus" className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">Drag to reposition • Zoom to fit • Rotate as needed</p>
                </div>

                {/* Toolbar */}
                <div className="flex flex-wrap gap-2 items-center">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setCropperRotation((prev) => prev - 90)}
                    disabled={organizerImageUploading}
                    className="px-3 py-2"
                  >
                    <Icon name="RotateCcw" className="w-4 h-4 mr-2" /> Rotate Left
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setCropperRotation((prev) => prev + 90)}
                    disabled={organizerImageUploading}
                    className="px-3 py-2"
                  >
                    <Icon name="RotateCw" className="w-4 h-4 mr-2" /> Rotate Right
                  </Button>

                  <div className="flex items-center gap-2 ml-auto">
                    <Button
                      type="button"
                      variant={cropShape === 'square' ? 'default' : 'outline'}
                      onClick={() => setCropShape('square')}
                      disabled={organizerImageUploading}
                      className="px-3 py-2"
                    >
                      <Icon name="Square" className="w-4 h-4 mr-2" /> Square
                    </Button>
                    <Button
                      type="button"
                      variant={cropShape === 'circle' ? 'default' : 'outline'}
                      onClick={() => setCropShape('circle')}
                      disabled={organizerImageUploading}
                      className="px-3 py-2"
                    >
                      <Icon name="Circle" className="w-4 h-4 mr-2" /> Circle
                    </Button>
                  </div>
                </div>

                <p className="text-xs text-muted-foreground">Export will use a circular mask and upload to Supabase storage.</p>

                <div className="flex justify-end gap-3 pt-2">
                  <Button
                    type="button"
                    onClick={() => {
                      setShowImageCropper(false);
                      setImageToCrop(null);
                      setCroppedResult(null);
                      setCropperZoom(1);
                      setCropperPanX(0);
                      setCropperPanY(0);
                      setCropperRotation(0);
                      setCropShape('circle');
                    }}
                    variant="outline"
                    disabled={organizerImageUploading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    onClick={applyCrop}
                    disabled={organizerImageUploading}
                  >
                    {organizerImageUploading ? 'Uploading...' : 'Apply'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Dialog */}
      <ConfirmDialog
        isOpen={showSuccessDialog}
        onClose={() => {
          setShowSuccessDialog(false);
          onClose();
        }}
        onConfirm={() => {
          setShowSuccessDialog(false);
          onClose();
        }}
        title="Success"
        message={successMessage}
        confirmLabel="OK"
        cancelLabel=""
        variant="default"
      />
    </div>
  );

};

export default WorkshopForm;
